import React, { useState } from 'react';
import { Alert, Button, Spinner, Table } from 'react-bootstrap';
import { ColumnDef } from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from 'utils/axiosInterceptors';
import { useBranchContext } from 'providers/BranchProvider';
import {
  TProcurementReturnForm,
  TProcurementReturnFormItem
} from 'utils/types';
import { formatDate } from 'utils/formats';
import DataTable from '../BranchProductInventory/DataTable';
import BadgeStatus from 'utils/BadgeStatus';

const ReturnFormsTab = () => {
  const { selectedBranch } = useBranchContext();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const formIdFromQuery = queryParams.get('formId');
  const [selectedFormId, setSelectedFormId] = useState<number | null>(
    formIdFromQuery ? Number(formIdFromQuery) : null
  );

  const openForm = (formId: number) => {
    setSelectedFormId(formId);
    const nextParams = new URLSearchParams(location.search);
    nextParams.set('formId', String(formId));
    navigate({ search: nextParams.toString() }, { replace: true });
  };

  const closeForm = () => {
    setSelectedFormId(null);
    const nextParams = new URLSearchParams(location.search);
    nextParams.delete('formId');
    navigate({ search: nextParams.toString() }, { replace: true });
  };

  const fetchForms = async (
    page: number,
    pageSize: number,
    search: string,
    orderBy: string,
    orderByDesc: boolean
  ) => {
    const { data } = await axiosInstance.get(
      `/api/v1/${selectedBranch?.branchDomain}/procurementReturnForms`,
      {
        params: {
          page,
          pageSize,
          search,
          orderBy,
          orderByDesc
        }
      }
    );
    return data;
  };

  const {
    data: formDetail,
    isLoading: detailLoading
  } = useQuery({
    queryKey: [
      'procurementReturnForm',
      selectedBranch?.branchDomain,
      selectedFormId
    ],
    enabled: !!selectedBranch?.branchDomain && selectedFormId != null,
    queryFn: async () => {
      const { data } = await axiosInstance.get<TProcurementReturnForm>(
        `/api/v1/${selectedBranch?.branchDomain}/procurementReturnForms/${selectedFormId}`
      );
      return data;
    }
  });

  const columns: ColumnDef<TProcurementReturnForm>[] = [
    {
      id: 'procurementReturnFormId',
      header: 'Form #',
      enableSorting: false,
      cell: ({ row: { original } }) => (
        <Button
          variant="link"
          className="p-0 text-decoration-none fw-bold fs-9"
          onClick={() => openForm(original.procurementReturnFormId)}
        >
          RF-{original.procurementReturnFormId}
        </Button>
      )
    },
    {
      id: 'supplierName',
      header: 'Supplier',
      enableSorting: false,
      cell: ({ row: { original } }) => (
        <Link
          to={`/supplier-details/${original.supplierId}`}
          className="text-decoration-none fw-bold fs-9"
        >
          {original.supplierName}
        </Link>
      )
    },
    {
      id: 'itemCount',
      header: 'Items',
      accessorKey: 'itemCount',
      enableSorting: false
    },
    {
      id: 'totalQuantity',
      header: 'Quantity',
      accessorKey: 'totalQuantity',
      enableSorting: false
    },
    {
      id: 'totalAmount',
      header: 'Total',
      enableSorting: false,
      cell: ({ row: { original } }) => (
        <span>{original.totalAmount?.toLocaleString()}</span>
      )
    },
    {
      id: 'createdByName',
      header: 'Created By',
      accessorKey: 'createdByName',
      enableSorting: false
    },
    {
      id: 'createdOn',
      header: 'Created On',
      enableSorting: false,
      cell: ({ row: { original } }) => (
        <span>{original.createdOn ? formatDate(original.createdOn) : '—'}</span>
      )
    }
  ];

  if (selectedFormId != null) {
    const items: TProcurementReturnFormItem[] = formDetail?.items || [];
    const total = items.reduce((sum, item) => sum + (item.amount || 0), 0);

    return (
      <div>
        <Button variant="link" className="p-0 mb-2" onClick={closeForm}>
          ← Back to return forms
        </Button>

        {detailLoading || !formDetail ? (
          <div className="py-5 text-center">
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <>
            <div className="mb-3">
              <h5 className="mb-1">
                Return Form RF-{formDetail.procurementReturnFormId}
              </h5>
              <div className="fs-9 text-body-secondary">
                Supplier:{' '}
                <Link
                  to={`/supplier-details/${formDetail.supplierId}`}
                  className="text-decoration-none fw-bold"
                >
                  {formDetail.supplierName}
                </Link>
                {' · '}
                Created {formatDate(formDetail.createdOn)}
                {formDetail.createdByName
                  ? ` by ${formDetail.createdByName}`
                  : ''}
              </div>
              {formDetail.notes && (
                <div className="fs-9 mt-2">Notes: {formDetail.notes}</div>
              )}
            </div>

            {items.length === 0 ? (
              <Alert variant="info" className="mb-0">
                This return form has no items.
              </Alert>
            ) : (
              <div className="table-responsive">
                <Table hover className="fs-9 align-middle">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Invoice #</th>
                      <th>Delivery Date</th>
                      <th>Invoice Date</th>
                      <th>Status</th>
                      <th>Qty</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(item => (
                      <tr key={item.procurementReturnId}>
                        <td>
                          <Link
                            to={`/product/${encodeURIComponent(
                              selectedBranch?.branchDomain as string
                            )}/${item.productId}`}
                            className="text-decoration-none fw-bold"
                          >
                            {item.productName}
                          </Link>
                        </td>
                        <td>
                          {item.procurementInvoiceId ? (
                            <Link
                              to={`/procurement-invoices/${item.procurementInvoiceId}`}
                              className="text-decoration-none"
                            >
                              {item.procurementInvoiceNumber ||
                                item.procurementInvoiceId}
                            </Link>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          {item.deliveredOn
                            ? formatDate(item.deliveredOn)
                            : '—'}
                        </td>
                        <td>
                          {item.invoicedOn
                            ? formatDate(item.invoicedOn)
                            : '—'}
                        </td>
                        <td>
                          <BadgeStatus status={item.status} />
                        </td>
                        <td>{item.quantity}</td>
                        <td>{item.amount?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={5} className="text-end fw-bold">
                        Total
                      </td>
                      <td className="fw-bold">
                        {items
                          .reduce((sum, i) => sum + (i.quantity || 0), 0)
                          .toLocaleString()}
                      </td>
                      <td className="fw-bold">{total.toLocaleString()}</td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <DataTable
      queryKey={['procurementReturnForms', selectedBranch?.branchDomain]}
      queryFn={fetchForms}
      columns={columns}
    />
  );
};

export default ReturnFormsTab;
